// /swiftboard/estimates — list of all estimates (PROTOTYPE, mock data).
import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calculator, Copy, RotateCcw, Settings2 } from "lucide-react";
import { AuthContext } from "../../../../../context/AuthContext";
import { useAlert } from "../../../../../context/AlertContext";
import TableGrid from "../../../components/TableGrid";
import TableToolBar from "../../../components/TableToolBar";
import Pagination from "../../../components/Pagination";
import NoDataState from "../../../components/NoDataState";
import StatusFilter from "../../../components/statusFilter";
import ConfirmDialogue from "../../../../../components/ui/ConfirmDialogue";
import { listEstimates, duplicateEstimate, resetMockData } from "../../mock/estimateService.mock";
import useAsync from "../../hooks/useAsync";
import { Button, IconButton, Pill } from "../../components/ui";
import { EstimateStatusPill, PrototypeBadge } from "../../components/EstimateBadges";
import { ErrorState } from "../../components/PageStates";
import { fmtCurrency, fmtDateTime, fmtInt } from "../../utils/format";

const STATUS_OPTIONS = ["ALL", "DRAFT", "FINALIZED"];

export default function EstimatesList() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { showAlert } = useAlert();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "updatedAt", direction: "desc" });
  const [resetTrigger, setResetTrigger] = useState(0);
  const [confirmReset, setConfirmReset] = useState(false);

  const { data, loading, error, reload } = useAsync(
    () => listEstimates({ search, status, skip: (page - 1) * pageSize, limit: pageSize }),
    [search, status, page, pageSize]
  );

  const rows = useMemo(() => {
    const list = [...(data?.data || [])];
    const { key, direction } = sortConfig;
    if (!key) return list;
    const val = (r) => (key === "bidTotal" ? r.totals.bidTotal : key === "pricePerSf" ? r.totals.bidTotal / (r.totals.paintableSf || 1) : r[key]);
    return list.sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return direction === "asc" ? cmp : -cmp;
    });
  }, [data, sortConfig]);

  const handleSort = (key) =>
    setSortConfig((s) => ({ key, direction: s.key === key && s.direction === "asc" ? "desc" : "asc" }));

  const openEstimate = (row) => navigate(`/swiftboard/estimates/${row.id}/takeoff`);

  const handleDuplicate = useCallback(
    async (row) => {
      try {
        const copy = await duplicateEstimate(row.id, [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "You");
        showAlert({ type: "success", message: `${row.id} duplicated as ${copy.id}` });
        navigate(`/swiftboard/estimates/${copy.id}/takeoff`);
      } catch (e) {
        showAlert({ type: "error", message: e.message });
      }
    },
    [user, showAlert, navigate]
  );

  const handleResetClose = async (confirmed) => {
    setConfirmReset(false);
    if (!confirmed) return;
    await resetMockData();
    showAlert({ type: "success", message: "Prototype data reset" });
    reload();
  };

  const columns = [
    {
      key: "id",
      label: "Estimate #",
      width: "110px",
      fixed: true,
      sortable: true,
      render: (row) => (
        <span className="inline-flex items-center rounded-[4px] border border-border bg-primary_tint px-2 py-0.5 font-mono text-xs font-medium text-primary">
          {row.id}
        </span>
      ),
    },
    {
      key: "jobTitle",
      label: "Job",
      width: "260px",
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium text-text_primary">
            #{row.jobId} · {row.jobTitle}
          </div>
          <div className="truncate text-[11.5px] text-text_muted">{row.project.projectName}</div>
        </div>
      ),
    },
    {
      key: "clientName",
      label: "Client / GC",
      width: "200px",
      sortable: true,
      render: (row) => <span className="truncate text-[12.5px] text-text_secondary">{row.clientName}</span>,
    },
    { key: "status", label: "Status", width: "120px", sortable: true, render: (row) => <EstimateStatusPill status={row.status} /> },
    {
      key: "bidTotal",
      label: "Bid Total",
      width: "140px",
      sortable: true,
      render: (row) => <span className="font-semibold tabular-nums text-text_primary">{fmtCurrency(row.totals.bidTotal)}</span>,
    },
    {
      key: "pricePerSf",
      label: "Price / SF",
      width: "100px",
      sortable: true,
      render: (row) => <span className="tabular-nums text-text_secondary">{fmtCurrency(row.totals.bidTotal / (row.totals.paintableSf || 1))}</span>,
    },
    {
      key: "lineCount",
      label: "Lines / SF",
      width: "120px",
      render: (row) => (
        <span className="tabular-nums text-[12px] text-text_secondary">
          {row.lineCount} · {fmtInt(row.totals.paintableSf)} SF
        </span>
      ),
    },
    {
      key: "configVersionId",
      label: "Config",
      width: "80px",
      render: (row) => <Pill tone="neutral">{row.configVersionId}</Pill>,
    },
    {
      key: "updatedAt",
      label: "Updated",
      width: "160px",
      sortable: true,
      render: (row) => <span className="text-[12px] text-text_secondary">{fmtDateTime(row.updatedAt)}</span>,
    },
  ];

  const renderActions = (row) => (
    <div className="flex items-center justify-center gap-2">
      <IconButton
        icon={Copy}
        size="xs"
        label={row.status === "FINALIZED" ? "Duplicate to revise" : "Duplicate"}
        onClick={(e) => {
          e.stopPropagation();
          handleDuplicate(row);
        }}
      />
      <Button
        variant="primary"
        size="xs"
        iconRight={ArrowRight}
        onClick={(e) => {
          e.stopPropagation();
          openEstimate(row);
        }}
      >
        Open
      </Button>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-[100rem] flex-col gap-4 py-3">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-[4px] bg-primary text-white">
              <Calculator size={18} aria-hidden="true" />
            </span>
            <div>
              <h1 className="flex items-center gap-2 text-[17px] font-semibold text-text_primary">
                Estimates <Pill tone="info">Beta</Pill>
              </h1>
              <p className="text-[12px] text-text_muted">Painting takeoff estimates. Start one from a job with a takeoff file.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" icon={RotateCcw} onClick={() => setConfirmReset(true)}>
              Reset demo data
            </Button>
            <Button icon={Settings2} onClick={() => navigate("/swiftboard/estimate-settings/global")}>
              Estimate Settings
            </Button>
            <Button variant="primary" icon={Calculator} onClick={() => navigate("/swiftboard/estimates/new/2918")} title="Prototype: Job 2918 has a takeoff file">
              New estimate (Job 2918)
            </Button>
          </div>
        </header>

        <TableToolBar
          onSearch={(v) => {
            setSearch(v);
            setPage(1);
          }}
          onRefresh={() => {
            setResetTrigger((n) => n + 1);
            setSearch("");
            setStatus("ALL");
            setPage(1);
          }}
          resetSearchTrigger={resetTrigger}
          canCreate={false}
          resultCount={data?.total}
        />

        <StatusFilter
          label="Filter by status:"
          options={STATUS_OPTIONS}
          selected={status}
          onChange={(s) => {
            setStatus(s);
            setPage(1);
          }}
        />

        {error ? (
          <ErrorState error={error} onRetry={reload} title="Could not load estimates" />
        ) : !loading && rows.length === 0 ? (
          <div className="flex items-center justify-center rounded-[4px] border border-border bg-surface">
            <NoDataState
              icon="file"
              title="No estimates found"
              message={search || status !== "ALL" ? "No estimates match your search or filter." : "Open a job with a takeoff file and choose Calculate Estimate."}
              showBack={false}
              showRefresh={false}
              size="medium"
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-[4px] border border-border">
              <TableGrid
                data={rows}
                loading={loading}
                columns={columns}
                sortConfig={sortConfig}
                onSort={handleSort}
                onRowClick={openEstimate}
                showActions
                selectable={false}
                renderActions={renderActions}
                pageSize={pageSize}
              />
            </div>
            <Pagination
              totalCount={data?.total || 0}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={(p, size = pageSize) => {
                if (size !== pageSize) {
                  setPageSize(size);
                  setPage(1);
                } else setPage(p);
              }}
            />
          </>
        )}
      </div>

      {confirmReset && (
        <ConfirmDialogue
          confirmData={{
            title: "Reset prototype data?",
            message: "All estimates you created in this browser tab are removed and the 4 sample estimates are restored.",
            confirmText: "Reset",
            variant: "warning",
          }}
          handleConfirmClose={handleResetClose}
        />
      )}
      <PrototypeBadge />
    </div>
  );
}
